/** @property csi/view/_query/LevelFilter */
define([
	"dojo/_base/declare",
	"dojo/dom-construct",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"csi",
	"csi/_base/underscore",
	"csi/config/preferences",
	"csi/core/Key",
	"csi/heading/_base",
	"csi/locale",
	"csi/middleware/customView/customViewDataManagementService",
	"csi/state/rootStore",
	"csi/utility/coreUtility",
	"csi/utility/operatorUtility",
	"csi/xml",
	"csi/xml/Query"
], function(declare, domConstruct, _TemplatedMixin, _WidgetBase, csi, _, configPreferences, Key, csiHeading, locale, {getRelativeFilterKeys}, {sessionStore}, {isNullOrUndefined}, {isBlankOperator}, xml, Query) {

	// Reference DataManagementSettings.ts
	var _CONDITIONS = "C";
	var _FLOATING_DATES = "FD";
	var _LOCALE = "L";
	var _OR = "OR";
	var _CURRENT_USER = "__USER__";

	var _FilterCondition = declare([_WidgetBase, _TemplatedMixin], {
		templateString: '<div>' +
			'<span data-dojo-attach-point="labelNode"></span> ' +
			'<i data-dojo-attach-point="opNode"></i> ' +
			'<span data-dojo-attach-point="valueNode"></span>' +
			'</div>',
		_setLabelAttr: {node: "labelNode", type: "innerText"},
		_setOpAttr: {node: "opNode", type: "innerText"},
		_setValueAttr: {node: "valueNode", type: "innerText"},

		postCreate() {
			if (this.start) {
				domConstruct.place(document.createTextNode("("), this.domNode, "first");
			}

			if (this.group) {
				this.group = " " + this.group;
			}

			if (this.end) {
				this.group = ")" + (this.group || "");
			}

			if (this.group) {
				domConstruct.place(document.createTextNode(this.group), this.domNode, "last");
			}

			if (this.value == null) {
				domConstruct.destroy(this.valueNode);
			}
		}
	});

	return declare(null, {
		/** @property {QueryLevel} ql - query level to which filters apply */
		ql: null,

		/** @property {csi.view.Widget} view - containing view */
		view: null,

		/** @property {Object} _data - custom view filters */
		_data: null,

		/**
		 * Constructor for query LevelFilter
		 * @param {csi.view.Widget} view
		 * @param {Object} data
		 * @param {QueryLevel} ql
		 */
		constructor(view, data, ql) {
			this.ql = ql;
			this.view = view;
			this._data = data;
		},

		/**
		 * Add dependency paths necessary to filter data if the client were to do it;
		 * in most cases the server does the filtering, but in some cases the client will do the work,
		 * especially when filtering a matrix
		 * @param {string[]} paths
		 */
		addPaths(paths) {
			if (!this._data) {
				return;
			}

			_.each(this._data[_CONDITIONS], (condObj) => {
				const spec = this.getFilterSpec(condObj.key);
				const path = spec?.getStandardQueryPath();
				if (path) {
					paths.push(path);
				}
			});
		},

		/**
		 * Add paths necessary to query to get relative values
		 * @param {string[]} paths
		 */
		addRelativePaths(paths) {
			// NOTE: "this.ql" does not yet exist when calling this function
			if (!this._data) {
				return;
			}

			_.each(this._data[_CONDITIONS], (condObj) => {
				if (condObj.relative && condObj.value) {
					// if condObj.value is an array, then use the first value
					condObj.value = Array.isArray(condObj.value) ? condObj.value[0] : condObj.value;

					const spec = this.getFilterSpec(condObj.value);
					const path = spec?.getStandardQueryPath();
					if (path) {
						paths.push(path);
					}
				}
			});
		},

		/**
		 * Return locale setting for custom view filters, if any
		 * @return {string | null}
		 */
		getLocale() {
			return this._data ? this._data[_LOCALE] : null;
		},

		/**
		 * Render filter settings in DOM
		 * @return {_FilterCondition[]}
		 */
		getFilterConditions() {
			const or = locale.resource("Or");
			const and = locale.resource("And");
			const widgets = [];

			// process custom view filters
			const conditions = this._data && this._data[_CONDITIONS];
			if (conditions) {
				const view = this.view;
				const ctx = view.getContext();
				const bFloating = this._data[_FLOATING_DATES];
				const bOr = this._data[_OR];
				let bGroup;

				const addFilterFunc = (condObj, isOr, bLast, group) => {
					const {key, op, relative, value} = condObj;
					let spec = this.getFilterSpec(key);
					if (!spec) {
						return;
					}

					const params = {
						label: spec.getExtendedName()
					};

					if (op.charAt(0) == "F") {
						params.op = locale.resource("OP_NOT_BLANK");
					} else if (op.charAt(0) == "U") {
						params.op = locale.resource("OP_BLANK");
					} else {
						params.op = locale.resource(spec.getLocalizationResBasedOnTypes(op));
					}

					if (group && !bGroup) {
						bGroup = true;
						params.start = true;
					} else if (bGroup && !group) {
						bGroup = false;
						params.end = true;
					}

					if (!bLast) {
						params.group = isOr ? or : and;
					}

					if (value != null) {
						if (relative) {
							const relativeKey = Array.isArray(value) ? value[0] : value;

							if (spec = this.getFilterSpec(relativeKey)) {
								// when relative, pass true to force appending the class name to the attribute name
								params.value = spec.getExtendedName(true);
							} else {
								params.value = locale.resource("Unknown");
							}
						} else if (value === _CURRENT_USER) {
							// TODO: Jake - value can be an array, right?
							params.value = locale.resource("PreferenceFilterCurrentUser");
						} else {
							params.value = spec.querySpec.ren.formatQueryFilterValue(ctx, value, spec.format, bFloating, true);
						}
					}

					widgets.push(new _FilterCondition(params));
				}

				_.each(conditions, (condObj, i) => {
					const bLast = i >= conditions.length - 1;
					if (condObj.conditions) {
						var n = condObj.conditions.length;
						condObj.conditions.forEach((obj, j) => {
							const bLastInGroup = j >= n - 1;
							addFilterFunc(obj, bLastInGroup ? bOr : !condObj.isAND, bLast && bLastInGroup, !bLastInGroup);
						});
					} else {
						addFilterFunc(condObj, bOr, bLast);
					}

				});
			}

			// render enforced user default filters that are not included in custom view filters - only need to handle boolean and enum values
			_.each(this.ql.enforcedUserDefaults, function(ud) {
				// TODO: would like to use something like spec.getExtendedName, but it does not work well for matrices because
				// we don't know if path leads into matrix. If this becomes a problem, then will have to come up with a solution for now,
				// but would rather invest time in fully moving to just extended/typed paths
				const type = ud.getTargetDestinationType();
				const label = locale.typeId(type, ud.targetAttributeId);

				if (ud.targetType != this.ql.type) {
					label = locale.resource("QueryLabelWithClass", [label, locale.type(type)]);
				}

				const textValues = ud.getTextValues();
				const nLast = textValues.length - 1;

				if (ud.allowBlanks) {
					widgets.push(new _FilterCondition({
						group: or,
						label: label,
						op: locale.resource("OP_BLANK")
					}));
				}

				_.each(textValues, function(textValue, i) {
					widgets.push(new _FilterCondition({
						group: i < nLast ? or : null,
						label: label,
						op: locale.resource(xml.OP_EQUALS),
						value: textValue
					}));
				})
			}, this);

			return widgets;
		},

		/**
		 * Resolve relative filter values - if any cannot be resolved, then return null
		 * @param {Context} [ctxRelative]
		 * @return {Object | null}
		 */
		getRelativeValues(ctxRelative) {
			const {view, _data} = this;

			if (!_data) {
				return null;
			}

			const relativeKeys = getRelativeFilterKeys(_data);
			ctxRelative ||= view.getQueryRelativeContext();

			if (!ctxRelative || !relativeKeys.length) {
				return null;
			}

			ctxRelative.locale = _data[_LOCALE];

			const values = {};
			const b = relativeKeys.every((key) => {
				const spec = this.getFilterSpec(key);
				if (!spec) {
					return false;
				}

				const value = values[key] = csiHeading.toFilterValue(ctxRelative, spec.heading);
				return value != null;
			});

			return b ? values : null;
		},

		/**
		 * Return spec for given key
		 * @param {C8AttributeKey | Key} key 
		 * @return {Spec} 
		 */
		getFilterSpec(key) {
			// NOTE: "this.ql" does not yet exist when calling addRelativePaths function
			return this.ql?.specBuilder.getCachedSpec(key, true) || this.view.specBuilder.getSpecForKey(key);
		},

		/**
		 * Return true if there are custom view filters for this level
		 * @return {boolean}
		 */
		hasCustomViewFilters() {
			return !!this._data;
		},

		/**
		 * Return true if filter conditions use relative filtering
		 * @return {boolean}
		 */
		hasRelativeFilters() {
			return !!getRelativeFilterKeys(this._data).length;
		},

		/**
		 * Populate query object from custom view and user defaults
		 * @param {Query} q
		 * @param {Spec} matrixSpec
		 */
		toMatrixQuery(q, matrixSpec) {
			if (this._data) {
				this._toMatrixQuery(q, matrixSpec);
			}

			// apply user default filters that must be enforced and do not appear as ad-hoc filters
			var ep = matrixSpec.matrixExtendedPath;
			if (ep) {
				configPreferences.applyUserDefaultsToQuery(q, ep.getEndType());
			}
		},

		/**
		 * Apply filters to query - if any cannot be applied because relative values do not exist, then ignore all filters
		 * @param {Query} qRoot
		 */
		toQuery(qRoot) {
			var b;

			if (this._data) {
				b = this._toQuery(qRoot);
			}

			return b ? qRoot : null;
		},

		/**
		 * Return value to compare against for given filter condition
		 * @param {Object} condObj
		 * @param {Object} [relativeValues]
		 * @return {any}
		 */
		_getConditionValue(condObj, relativeValues) {
			let v = condObj.value;

			if (isNullOrUndefined(v)) {
				return v;
			}

			if (condObj.relative) {
				v = relativeValues[v];
			} else if (v == _CURRENT_USER) {
				v = sessionStore.currentUser.$Name;
			}

			if ((condObj.op == xml.OP_MATCHES) || (condObj.op == xml.OP_NOT_MATCHES)) {
				if (_.isArray(v)) {
					v = _.map(v, xml.escapeMatchValue);
				} else {
					v = xml.escapeMatchValue(v);
				}
			}

			return v;
		},

		/**
		 * Populate query object for non-grouped filters that apply to matrix columns; filters using OR are ignored
		 * @param {Query} q
		 * @param {Spec} matrixSpec
		 */
		_toMatrixQuery(q, matrixSpec) {
			var bOr = this._data[_OR];
			var conditions = this._data[_CONDITIONS];

			// if OR condition, then ignore if more than one condition 
			if (bOr && (conditions.length > 1)) {
				return;
			}

			var epMatrix = matrixSpec.matrixExtendedPath;
			var relativeValues;
			var filterParams = {
				locale: this._data[_LOCALE]
			};
			var qGroup;
			var bApplyGroup;

			var processGroup = function(condObj) {
				// if reached last condition of group, then apply query if all conditions in group belong to the matrix and reset
				if (qGroup && !condObj.group) {
					if (bApplyGroup) {
						q.Or().importQuery(qGroup);
					}

					qGroup = null;
				}
			};

			_.each(conditions, (condObj) => {
				// track group filters in a separate query until done
				if (condObj.group && !qGroup) {
					qGroup = new Query();
					bApplyGroup = true;
				}

				const spec = this.getFilterSpec(condObj.key);
				if (!spec) {
					return;
				}

				if (spec.parentSpec != matrixSpec) {
					// it is possible the filter attribute is not in the view, in which case it will not be related to matrixSpec
					const ep = spec.getExtendedPath();
					const qs = spec.querySpec;

					// ignore types when comparing paths as the types could include derived classes and not be truly equal;
					// it is only the path steps that need to be equal
					if (ep && epMatrix && ep.isAlongPath(epMatrix, true) && !qs.pathInMatrix) {
						// do one more check to ensure the path applies to this matrix - it is possible that there
						// are two matrices along the same path and so we should disregard if remaining path still has 0..* steps
						// which indicates it is for a different matrix; clone the path so that it doesn't have the cached Spec
						// For example:
						// <Heading Name="ProductSourcesMatrix" Path="Child:ProductSourcing/Child:ProductSources" IsMatrixGroup="1" Hidden="true">
						// <Heading Name="SamplesMatrix" Path="Child:ProductSourcing/Child:ProductSources/Child:Samples" IsMatrixGroup="1" Hidden="true">
						const epInMatrix = spec.getExtendedQueryPath()?.clone();

						epInMatrix?.shiftSteps(epMatrix.depth, true);

						if (epInMatrix?.isSingle) {
							// ensure that pathInMatrix is populated on Spec
							qs.pathInMatrix = epInMatrix.getQueryPath();
						} else {
							return;
						}
					} else {
						// ignore any filters outside the matrix
						bApplyGroup = false;
						processGroup(condObj);
						return;
					}
				}

				if (condObj.relative) {
					// apply filters only if all relative values were resolved
					relativeValues ||= this.getRelativeValues();

					if (!relativeValues) {
						processGroup(condObj);
						return;
					}
				}

				const v = this._getConditionValue(condObj, relativeValues);
				spec.applyFilter(qGroup || q, condObj.op, v, true, false, filterParams);
				processGroup(condObj);
			});
		},

		/**
		 * Populate query and return true if at least one condition was applied
		 * @param {Query} qRoot
		 * @return {boolean}
		 */
		_toQuery(qRoot) {
			var bOr = this._data[_OR];
			var qCurrent = bOr ? qRoot.Or() : qRoot;
			var qMain = qCurrent;
			var relativeValues;

			var filterParams = {
				locale: this._data[_LOCALE]
			};

			// apply filters only if all relative values were resolved
			var b = _.every(this._data[_CONDITIONS], (condObj) => {
				const spec = this.getFilterSpec(condObj.key);
				if (!spec && !condObj.conditions) {
					// If the condition is not a group condition and
					// if spec is no longer part of model or view, then ignore (should not stop others from being applied)
					return true;
				}

				if (condObj.relative) {
					relativeValues ||= this.getRelativeValues();

					if (!relativeValues) {
						return false;
					}
				}

				if (condObj.conditions) {
					qCurrent = condObj.isAND ? qMain.And() : qMain.Or();
					_.forEach(condObj.conditions, (filter) => {
						// if the user did not select a value for the filter and its operator is not a blank operator, ignore
						// when the user selects a date attribute, the default value will be an empty array, so it is also checked after checking null
						if ((isNullOrUndefined(filter.value) || filter.value?.length === 0) && !isBlankOperator(filter.op)) {
							return true
						}
						const key = new Key({dpkey: filter.key});
						const spec = this.getFilterSpec(key);
						if (filter.relative) {
							relativeValues ||= this.getRelativeValues();

							if (!relativeValues) {
								return;
							}
						}
						const v = this._getConditionValue(filter, relativeValues);
						spec.applyFilter(qCurrent, filter.op, v, false, false, filterParams);
					})
					qCurrent = qMain;
				} else {
					// if the user did not select a value for the filter and its operator is not a blank operator, ignore
					// when the user selects a date attribute, the default value will be an empty array, so it is also checked after checking null
					if ((isNullOrUndefined(condObj.value) || condObj.value?.length === 0) && !isBlankOperator(condObj.op)) {
						return true
					}
					const v = this._getConditionValue(condObj, relativeValues);
					spec.applyFilter(qCurrent, condObj.op, v, false, false, filterParams);
				}

				return true;
			});

			return b;
		}
	});
});
